<template>
  <div class="shell">
    <!-- Top bar -->
    <header class="topbar">
      <LgIconButton class="only-mobile" label="Open navigation" @click="mobileNav = true">
        <Menu :size="20" aria-hidden="true" />
      </LgIconButton>

      <span class="brand">
        <GoblinIcon class="brand-mark" variant="mark" size="1.4em" label="loggob" />
        <span class="brand-word">loggob</span>
      </span>
      <span v-if="title" class="title" aria-current="page">{{ title }}</span>

      <div class="topbar-slot">
        <slot name="toolbar" />
      </div>

      <div class="topbar-right">
        <LgMenu :items="userMenuItems" align="right" @select="onUserSelect">
          <template #trigger="{ open }">
            <button class="user-btn" type="button" :aria-expanded="open" aria-haspopup="menu">
              <User :size="16" aria-hidden="true" />
              <span class="user-name">{{ username || 'Account' }}</span>
              <ChevronDown :size="15" aria-hidden="true" />
            </button>
          </template>
        </LgMenu>
      </div>
    </header>

    <div class="shell-body">
      <!-- Desktop nav rail -->
      <nav class="navrail only-desktop" :class="{ expanded: railExpanded }" aria-label="Sections">
        <ul class="navlist">
          <template v-for="(group, gi) in navGroups" :key="`g${gi}`">
            <li v-if="gi > 0" class="nav-divider" aria-hidden="true" />
            <li v-for="item in group" :key="item.key">
              <LgTooltip placement="right" :text="railExpanded ? '' : item.label">
                <button
                  class="navitem"
                  type="button"
                  :class="{ active: item.active }"
                  :aria-label="item.label"
                  :aria-current="item.active ? 'page' : undefined"
                  @click="onNavClick(item)"
                >
                  <component :is="item.icon" :size="20" aria-hidden="true" />
                  <span v-if="railExpanded" class="navitem-label">{{ item.label }}</span>
                </button>
              </LgTooltip>
            </li>
          </template>
        </ul>

        <!-- Settings sits below a divider, visually separated from destinations. -->
        <div class="rail-foot">
          <div class="nav-divider" aria-hidden="true" />
          <LgTooltip placement="right" :text="railExpanded ? '' : 'Settings'">
            <button
              class="navitem"
              type="button"
              aria-label="Settings"
              :class="{ active: currentView === 'settings' }"
              :aria-current="currentView === 'settings' ? 'page' : undefined"
              @click="onSettings"
            >
              <Settings :size="20" aria-hidden="true" />
              <span v-if="railExpanded" class="navitem-label">Settings</span>
            </button>
          </LgTooltip>

          <button
            class="rail-toggle"
            type="button"
            :aria-label="railExpanded ? 'Collapse navigation' : 'Expand navigation'"
            @click="railExpanded = !railExpanded"
          >
            <PanelLeftClose v-if="railExpanded" :size="18" aria-hidden="true" />
            <PanelLeftOpen v-else :size="18" aria-hidden="true" />
          </button>
        </div>
      </nav>

      <main class="shell-content">
        <slot />
      </main>
    </div>

    <!-- Mobile nav drawer -->
    <LgDrawer :open="mobileNav" side="left" title="loggob" @close="mobileNav = false">
      <ul class="drawer-navlist">
        <template v-for="(group, gi) in navGroups" :key="`dg${gi}`">
          <li v-if="gi > 0" class="drawer-divider" aria-hidden="true" />
          <li v-for="item in group" :key="item.key">
            <button
              class="drawer-navitem"
              type="button"
              :class="{ active: item.active }"
              :aria-current="item.active ? 'page' : undefined"
              @click="onNavClick(item)"
            >
              <component :is="item.icon" :size="20" aria-hidden="true" />
              <span>{{ item.label }}</span>
            </button>
          </li>
        </template>

        <li class="drawer-divider" aria-hidden="true" />
        <li>
          <button
            class="drawer-navitem"
            type="button"
            :class="{ active: currentView === 'settings' }"
            :aria-current="currentView === 'settings' ? 'page' : undefined"
            @click="onSettings"
          >
            <Settings :size="20" aria-hidden="true" />
            <span>Settings</span>
          </button>
        </li>
      </ul>
    </LgDrawer>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import {
  House,
  LayoutDashboard,
  ChartSpline,
  ScrollText,
  Waypoints,
  BellRing,
  Menu,
  User,
  ChevronDown,
  Settings,
  LogOut,
  PanelLeftClose,
  PanelLeftOpen,
} from 'lucide-vue-next';
import LgIconButton from './ui/LgIconButton.vue';
import LgTooltip from './ui/LgTooltip.vue';
import LgMenu, { type MenuItem } from './ui/LgMenu.vue';
import LgDrawer from './ui/LgDrawer.vue';
import GoblinIcon from './components/GoblinIcon.vue';
import type { View } from './views.js';

const props = withDefaults(
  defineProps<{
    title?: string;
    username?: string;
    currentView?: View;
  }>(),
  { title: 'Logs', currentView: 'logs' },
);

const emit = defineEmits<{ navigate: [view: View]; 'sign-out': [] }>();

const railExpanded = ref(false);
const mobileNav = ref(false);

interface NavItem {
  key: View;
  label: string;
  icon: unknown;
  active: boolean;
}

/** Six destinations, grouped by hairline dividers into three intents
 * (per ux-spec §1.2): curated (Home/Dashboards) · raw signals
 * (Metrics/Logs/Traces) · acts-on-your-behalf (Alerts). */
const navGroups = computed<NavItem[][]>(() => {
  const item = (key: View, label: string, icon: unknown): NavItem => ({
    key,
    label,
    icon,
    active: props.currentView === key,
  });
  return [
    [item('home', 'Home', House), item('dashboards', 'Dashboards', LayoutDashboard)],
    [
      item('metrics', 'Metrics', ChartSpline),
      item('logs', 'Logs', ScrollText),
      item('traces', 'Traces', Waypoints),
    ],
    [item('alerts', 'Alerts', BellRing)],
  ];
});

const userMenuItems = computed<MenuItem[]>(() => [
  { label: 'Settings', value: 'settings', icon: Settings },
  { label: 'Sign out', value: 'sign-out', icon: LogOut, danger: true },
]);

function onNavClick(item: NavItem) {
  mobileNav.value = false;
  if (item.active) return;
  emit('navigate', item.key);
}

function onSettings() {
  mobileNav.value = false;
  if (props.currentView !== 'settings') emit('navigate', 'settings');
}

function onUserSelect(value: string) {
  if (value === 'settings') emit('navigate', 'settings');
  else if (value === 'sign-out') emit('sign-out');
}
</script>

<style scoped>
.shell {
  display: flex;
  flex-direction: column;
  height: 100vh;
}

/* Top bar */
.topbar {
  display: flex;
  align-items: center;
  gap: var(--lg-space-3);
  height: var(--lg-topbar-h);
  padding: 0 var(--lg-space-3);
  flex-shrink: 0;
  background: var(--lg-bg-raised);
  border-bottom: 1px solid var(--lg-border);
  z-index: var(--lg-z-sticky);
}
.brand {
  display: inline-flex;
  align-items: center;
  gap: var(--lg-space-2);
  font-family: var(--lg-font-mono);
  font-size: var(--lg-fs-lg);
  font-weight: 700;
  letter-spacing: -0.02em;
}
.brand-mark {
  color: var(--lg-accent);
  flex-shrink: 0;
}
.brand-word {
  line-height: 1;
}
.title {
  color: var(--lg-text-dim);
  font-size: var(--lg-fs-md);
  padding-left: var(--lg-space-3);
  border-left: 1px solid var(--lg-border);
}
.topbar-slot {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
}
.topbar-right {
  flex-shrink: 0;
}
.user-btn {
  display: inline-flex;
  align-items: center;
  gap: var(--lg-space-2);
  min-height: var(--lg-control-md);
  padding: 0 var(--lg-space-2);
  border-radius: var(--lg-radius-md);
  border: 1px solid var(--lg-border-strong);
  background: var(--lg-bg-surface);
  color: var(--lg-text);
  font-family: var(--lg-font-sans);
  font-size: var(--lg-fs-sm);
  cursor: pointer;
}
.user-btn:hover {
  border-color: var(--lg-accent);
}
.user-btn:focus-visible {
  outline: none;
  box-shadow: var(--lg-focus);
}
.user-name {
  max-width: 140px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.shell-body {
  display: flex;
  flex: 1;
  min-height: 0;
}

/* Nav rail */
.navrail {
  display: flex;
  flex-direction: column;
  width: var(--lg-navrail-w);
  flex-shrink: 0;
  background: var(--lg-bg-raised);
  border-right: 1px solid var(--lg-border);
  transition: width var(--lg-dur-base) var(--lg-ease);
}
.navrail.expanded {
  width: var(--lg-navrail-w-expanded);
}
.navlist {
  flex: 1;
  list-style: none;
  margin: 0;
  padding: var(--lg-space-2);
  display: flex;
  flex-direction: column;
  gap: var(--lg-space-1);
  overflow-y: auto;
}
.navlist > li {
  display: block;
}
.navlist :deep(.lg-tt) {
  display: flex;
  width: 100%;
}
.nav-divider {
  height: 1px;
  margin: var(--lg-space-1) var(--lg-space-2);
  background: var(--lg-border);
}
.rail-foot {
  flex-shrink: 0;
  padding: var(--lg-space-2);
  display: flex;
  flex-direction: column;
  gap: var(--lg-space-1);
}
.rail-foot .nav-divider {
  margin: 0 var(--lg-space-1) var(--lg-space-1);
}
.rail-foot :deep(.lg-tt) {
  display: flex;
  width: 100%;
}
.navitem {
  display: flex;
  align-items: center;
  gap: var(--lg-space-3);
  width: 100%;
  min-height: var(--lg-touch);
  padding: 0 var(--lg-space-3);
  border: none;
  border-radius: var(--lg-radius-md);
  background: transparent;
  color: var(--lg-text-dim);
  font-family: var(--lg-font-sans);
  font-size: var(--lg-fs-md);
  cursor: pointer;
  transition:
    color var(--lg-dur-fast) var(--lg-ease),
    background var(--lg-dur-fast) var(--lg-ease);
}
.navitem:hover:not(.active) {
  color: var(--lg-text);
  background: var(--lg-bg-hover);
}
.navitem.active {
  color: var(--lg-accent);
  background: var(--lg-bg-active);
  box-shadow: inset 3px 0 0 var(--lg-accent);
}
.navitem-label {
  flex: 1;
  text-align: left;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.rail-toggle {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: var(--lg-touch);
  border: none;
  border-radius: var(--lg-radius-md);
  background: transparent;
  color: var(--lg-text-dim);
  cursor: pointer;
}
.rail-toggle:hover {
  color: var(--lg-text);
  background: var(--lg-bg-hover);
}
.rail-toggle:focus-visible {
  outline: none;
  box-shadow: var(--lg-focus);
}

.shell-content {
  flex: 1;
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

/* Mobile nav drawer list */
.drawer-navlist {
  list-style: none;
  margin: 0;
  padding: var(--lg-space-2);
  display: flex;
  flex-direction: column;
  gap: var(--lg-space-1);
}
.drawer-divider {
  height: 1px;
  margin: var(--lg-space-1) var(--lg-space-2);
  background: var(--lg-border);
}
.drawer-navitem {
  display: flex;
  align-items: center;
  gap: var(--lg-space-3);
  width: 100%;
  min-height: var(--lg-touch);
  padding: 0 var(--lg-space-3);
  border: none;
  border-radius: var(--lg-radius-md);
  background: transparent;
  color: var(--lg-text-dim);
  font-family: var(--lg-font-sans);
  font-size: var(--lg-fs-base);
  cursor: pointer;
}
.drawer-navitem > span:first-of-type {
  flex: 1;
  text-align: left;
}
.drawer-navitem.active {
  color: var(--lg-accent);
  background: var(--lg-bg-active);
}

/* Responsive visibility */
.only-mobile {
  display: none;
}
@media (max-width: 768px) {
  .only-desktop {
    display: none;
  }
  .only-mobile {
    display: inline-flex;
  }
  .title {
    display: none;
  }
}
</style>
